/** 门户图文热点：HTML 构建、兜底修复与静态点击联动 */

import { buildProductLinkCardHtml } from '@/components/shared/utils/productLinkCards'
import { ensureTableTabsDisplayHtml, upgradeLegacyTablesInHtml } from '@/components/shared/utils/tableTabs'
import { formatViewingHotspot } from '@/components/shared/utils/richContentUiLabels'
import { parseHtmlFragment, relocateInlineStylesInHtml } from '@/components/shared/utils/promoteInlineStyles'
import { allocateHotspotAnchorId } from '@/components/shared/utils/hotspotAnchor'

export const HOTSPOT_TABS_TYPE = 'hotspot-tabs'

const HOTSPOT_HEADING_FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px']
const DEFAULT_HEADING_FONT_SIZE = '18px'

const clampPct = (value) => Math.min(96, Math.max(4, Number(value) || 0))
const round1 = (value) => Math.round(value * 10) / 10

function normalizeHeading(value) {
  return typeof value === 'string' ? value : ''
}

function normalizeHeadingVisible(value) {
  if (value === false || value === 'false' || value === 0 || value === '0') return false
  return true
}

function normalizeHeadingStyle(value) {
  const src = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  let fontSize = typeof src.fontSize === 'string' ? src.fontSize.trim() : ''
  if (!HOTSPOT_HEADING_FONT_SIZES.includes(fontSize)) fontSize = DEFAULT_HEADING_FONT_SIZE
  return {
    fontSize,
    bold: src.bold === true || src.bold === 'true' || src.bold === 1,
    italic: src.italic === true || src.italic === 'true' || src.italic === 1,
  }
}

function headingStyleToCss(style) {
  const normalized = normalizeHeadingStyle(style)
  return `font-size:${normalized.fontSize};font-weight:${normalized.bold ? 700 : 400};font-style:${normalized.italic ? 'italic' : 'normal'}`
}

function shouldRenderHotspotHeading(attrs = {}) {
  return normalizeHeadingVisible(attrs.headingVisible)
    && Boolean(String(normalizeHeading(attrs.heading)).trim())
}

function buildHotspotHeadingHtml(attrs = {}, { anchorIds } = {}) {
  if (!shouldRenderHotspotHeading(attrs)) return ''
  const text = escapeHtml(String(normalizeHeading(attrs.heading)).trim())
  const style = headingStyleToCss(attrs.headingStyle)
  const used = anchorIds instanceof Set ? anchorIds : null
  const anchorId = used
    ? allocateHotspotAnchorId(normalizeHeading(attrs.heading), used)
    : ''
  const idAttr = anchorId ? ` id="${escapeHtml(anchorId)}"` : ''
  return `<div class="hotspot-heading"${idAttr} style="${style}">${text}</div>`
}

function syncHotspotHeadingElement(root, config, doc, anchorIds) {
  const existing = root.querySelector(':scope > .hotspot-heading')
  if (!shouldRenderHotspotHeading(config)) {
    existing?.remove()
    return
  }
  const text = String(normalizeHeading(config.heading) || '').trim()
  const css = headingStyleToCss(config.headingStyle)
  let el = existing
  if (!el) {
    el = doc.createElement('div')
    el.className = 'hotspot-heading'
    root.insertBefore(el, root.firstChild)
  } else if (el !== root.firstChild) {
    root.insertBefore(el, root.firstChild)
  }
  el.setAttribute('style', css)
  el.textContent = text
  if (anchorIds instanceof Set) {
    const anchorId = allocateHotspotAnchorId(text, anchorIds)
    if (anchorId) el.setAttribute('id', anchorId)
    else el.removeAttribute('id')
  }
}

function emptyHotspotTab(title = '') {
  return {
    title,
    content: '',
    links: [],
    linkType: '',
    linkId: null,
    linkTitle: '',
    linkImage: '',
    linkSubtitle: '',
    productId: null,
    productName: '',
    productCover: '',
    showLabel: true,
  }
}

function normalizeImages(attrs = {}) {
  let images = []
  if (Array.isArray(attrs.images)) {
    images = attrs.images
      .filter((item) => typeof item === 'string' && item.trim())
      .map((item) => item.trim())
  }
  if (!images.length && typeof attrs.src === 'string' && attrs.src.trim()) {
    images = [attrs.src.trim()]
  }
  return images
}

function normalizeSpots(spots, imageCount = 0) {
  if (!Array.isArray(spots)) return []
  const maxIdx = imageCount > 0 ? imageCount - 1 : 0
  return spots.map((spot) => {
    let imageIndex = Math.floor(Number(spot?.imageIndex))
    if (!Number.isFinite(imageIndex) || imageIndex < 0) imageIndex = 0
    if (imageCount > 0) imageIndex = Math.min(imageIndex, maxIdx)
    return {
      x: round1(clampPct(spot?.x)),
      y: round1(clampPct(spot?.y)),
      imageIndex,
    }
  })
}

function normalizeLinkType(value) {
  if (value === 'product-category' || value === 'category') return 'product-category'
  if (value === 'product') return 'product'
  return ''
}

function normalizeLinkItem(item) {
  if (!item || typeof item !== 'object') return null
  const type = normalizeLinkType(item.type || item.linkType)
  const id = Number(item.id ?? item.linkId)
  if (!type || !Number.isFinite(id) || id <= 0) return null
  return {
    type,
    id,
    title: typeof item.title === 'string' ? item.title : (item.linkTitle || ''),
    image: typeof item.image === 'string' ? item.image : (item.linkImage || item.imageUrl || ''),
    subtitle: typeof item.subtitle === 'string' ? item.subtitle : (item.linkSubtitle || ''),
    certifications: normalizeLinkCertUrls(item.certifications),
  }
}

function normalizeLinkCertUrls(list) {
  if (!Array.isArray(list)) return []
  const urls = []
  const seen = new Set()
  for (const entry of list) {
    const url = typeof entry === 'string'
      ? entry.trim()
      : String(entry?.url || entry?.thumbnailUrl || entry?.fileUrl || '').trim()
    if (!url || seen.has(url)) continue
    seen.add(url)
    urls.push(url)
  }
  return urls
}

function normalizeTabLinks(tab = {}) {
  if (Array.isArray(tab.links) && tab.links.length) {
    const list = tab.links.map(normalizeLinkItem).filter(Boolean)
    const seen = new Set()
    return list.filter((item) => {
      const key = `${item.type}:${item.id}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  let linkType = normalizeLinkType(tab?.linkType)
  let linkId = Number(tab?.linkId)
  let linkTitle = typeof tab?.linkTitle === 'string' ? tab.linkTitle : ''
  let linkImage = typeof tab?.linkImage === 'string' ? tab.linkImage : ''
  let linkSubtitle = typeof tab?.linkSubtitle === 'string' ? tab.linkSubtitle : ''

  const legacyProductId = Number(tab?.productId)
  if (!linkType && Number.isFinite(legacyProductId) && legacyProductId > 0) {
    linkType = 'product'
    linkId = legacyProductId
    if (!linkTitle) linkTitle = typeof tab?.productName === 'string' ? tab.productName : ''
    if (!linkImage) linkImage = typeof tab?.productCover === 'string' ? tab.productCover : ''
  }

  if (!linkType || !Number.isFinite(linkId) || linkId <= 0) return []

  return [{
    type: linkType,
    id: linkId,
    title: linkTitle,
    image: linkImage,
    subtitle: linkSubtitle,
    certifications: [],
  }]
}

function syncLegacyLinkFields(tab = {}, links = []) {
  const first = links[0]
  if (!first) {
    return {
      linkType: '',
      linkId: null,
      linkTitle: '',
      linkImage: '',
      linkSubtitle: '',
      productId: null,
      productName: '',
      productCover: '',
    }
  }
  return {
    linkType: first.type,
    linkId: first.id,
    linkTitle: first.title,
    linkImage: first.image,
    linkSubtitle: first.subtitle,
    productId: first.type === 'product' ? first.id : null,
    productName: first.type === 'product' ? first.title : '',
    productCover: first.type === 'product' ? first.image : '',
  }
}

function normalizeTabs(tabs, { defaultShowLabel = true } = {}) {
  if (!Array.isArray(tabs)) return []
  return tabs.map((tab) => {
    const links = normalizeTabLinks(tab)
    const legacy = syncLegacyLinkFields(tab, links)

    return {
      title: typeof tab?.title === 'string' ? tab.title : '',
      content: typeof tab?.content === 'string' ? tab.content : '',
      links,
      showLabel: tabShowLabel(tab, defaultShowLabel),
      ...legacy,
    }
  })
}

function syncSpotsAndTabs(attrs = {}) {
  const images = normalizeImages(attrs)
  const spots = normalizeSpots(attrs.spots, images.length)
  const globalShowLabel = normalizeShowLabels(attrs.showLabels)
  const tabs = normalizeTabs(attrs.tabs, { defaultShowLabel: globalShowLabel })
  const count = Math.max(spots.length, tabs.length)
  while (spots.length < count) {
    const i = spots.length
    spots.push({
      x: round1(20 + (i % 5) * 12),
      y: round1(20 + (i % 4) * 15),
      imageIndex: 0,
    })
  }
  while (tabs.length < count) {
    const i = tabs.length
    tabs.push(emptyHotspotTab(`条目${i + 1}`))
  }
  return {
    images,
    src: images[0] || '',
    spots,
    tabs,
  }
}

const HOTSPOT_MEDIA_POSITIONS = ['left', 'right', 'top', 'bottom']
const HOTSPOT_CERTS_TITLE = 'Approvals / Certifications'
const HOTSPOT_CERT_ALIGNS = ['left', 'center', 'right']

function normalizeMediaPosition(value) {
  const pos = String(value || '').toLowerCase()
  return HOTSPOT_MEDIA_POSITIONS.includes(pos) ? pos : 'left'
}

function normalizeCertAlign(value) {
  const align = String(value || '').toLowerCase()
  return HOTSPOT_CERT_ALIGNS.includes(align) ? align : 'left'
}

function normalizeShowLabels(value) {
  if (value === false || value === 'false' || value === 0) return false
  return true
}

function tabShowLabel(tab, fallback = true) {
  if (tab && tab.showLabel !== undefined) return normalizeShowLabels(tab.showLabel)
  if (tab && tab.showLabels !== undefined) return normalizeShowLabels(tab.showLabels)
  return normalizeShowLabels(fallback)
}

function normalizeCertifications(attrs = {}) {
  if (!Array.isArray(attrs.certifications)) return []
  return attrs.certifications
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => item.trim())
}

/** 取正文中第一个图文热点的认证图 URL，供产品列表卡片使用 */
export function extractCertificationsFromBodyJson(bodyJson) {
  let doc = bodyJson
  if (typeof bodyJson === 'string') {
    try {
      doc = JSON.parse(bodyJson)
    } catch {
      return []
    }
  }
  const urls = []
  const seen = new Set()
  const walk = (node) => {
    if (!node || urls.length) return
    if (node.type === 'hotspotTabs') {
      for (const url of normalizeCertifications(node.attrs || {})) {
        if (seen.has(url)) continue
        seen.add(url)
        urls.push(url)
      }
      if (urls.length) return
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child)
        if (urls.length) return
      }
    }
  }
  walk(doc)
  return urls
}

function normalizeDefaultContent(attrs = {}) {
  return typeof attrs.defaultContent === 'string' ? attrs.defaultContent : ''
}

function normalizeDefaultLinks(attrs = {}) {
  return normalizeTabLinks({ links: attrs.defaultLinks })
}

function buildHotspotConfig(attrs = {}) {
  const synced = syncSpotsAndTabs(attrs)
  return {
    src: synced.src,
    images: synced.images,
    mediaPosition: normalizeMediaPosition(attrs.mediaPosition),
    certAlign: normalizeCertAlign(attrs.certAlign),
    showLabels: normalizeShowLabels(attrs.showLabels),
    spots: synced.spots,
    tabs: synced.tabs,
    certifications: normalizeCertifications(attrs),
    defaultContent: normalizeDefaultContent(attrs),
    defaultLinks: normalizeDefaultLinks(attrs),
    heading: normalizeHeading(attrs.heading),
    headingVisible: normalizeHeadingVisible(attrs.headingVisible),
    headingStyle: normalizeHeadingStyle(attrs.headingStyle),
  }
}

/** 展示态初始：有默认正文或无标点时显示默认面板，否则兼容旧数据显示首个标点 */
function resolveHotspotInitialIndex(config = {}) {
  const hasDefault = Boolean(String(config.defaultContent || '').trim())
  const tabCount = Array.isArray(config.tabs) ? config.tabs.length : 0
  if (hasDefault || !tabCount) return -1
  return 0
}

function buildHotspotCertificationsHtml(certifications = [], { resolveSrc, align } = {}) {
  const list = normalizeCertifications({ certifications })
  if (!list.length) return ''
  const resolve = typeof resolveSrc === 'function' ? resolveSrc : (src) => src || ''
  const certAlign = normalizeCertAlign(align)
  const items = list
    .map((url) => {
      const src = resolve(url)
      if (!src) return ''
      return (
        `<div class="hotspot-cert-item">` +
        `<img src="${escapeHtml(src)}" alt="" class="hotspot-cert-img" loading="lazy" />` +
        `</div>`
      )
    })
    .filter(Boolean)
    .join('')
  if (!items) return ''
  return (
    `<div class="hotspot-certs is-align-${certAlign}" data-cert-align="${certAlign}">` +
    `<div class="hotspot-certs-head">` +
    `<div class="hotspot-certs-title">${escapeHtml(HOTSPOT_CERTS_TITLE)}</div>` +
    `</div>` +
    `<div class="hotspot-certs-list">${items}</div>` +
    `</div>`
  )
}

export function productPortalUrl(productId) {
  return `/products/detail/${productId}`
}

function buildSingleHotspotDeviceCardHtml(link = {}, { resolveSrc, mode = 'full' } = {}) {
  const normalized = normalizeLinkItem(link)
  if (!normalized) return ''
  if (mode === 'none') return ''

  const isCategory = normalized.type === 'product-category'
  const label = isCategory ? '产品分类' : '产品'
  const title = normalized.title || label

  if (mode === 'placeholder') {
    return (
      `<div class="hotspot-device-card-placeholder" data-hotspot-device-placeholder="1">` +
      `<span class="hotspot-device-card-placeholder__tag">${escapeHtml(label)}</span>` +
      `<span class="hotspot-device-card-placeholder__name">${escapeHtml(title)}</span>` +
      `<span class="hotspot-device-card-placeholder__tip">门户将展示设备卡片</span>` +
      `</div>`
    )
  }

  const resolve = typeof resolveSrc === 'function' ? resolveSrc : (src) => src || ''
  const cardHtml = buildProductLinkCardHtml({
    href: isCategory
      ? `/products/category/${normalized.id}`
      : `/products/detail/${normalized.id}`,
    title,
    subtitle: normalized.subtitle || '',
    image: resolve(normalized.image),
    linkType: isCategory ? 'product-category' : 'product',
    productId: isCategory ? '' : normalized.id,
    categoryId: isCategory ? normalized.id : '',
    certifications: isCategory ? [] : (normalized.certifications || []),
  }, { compact: true })
  return `<div class="rich-body-product-card-wrap hotspot-device-card">${cardHtml}</div>`
}

function buildHotspotDeviceCardHtml(tab = {}, { resolveSrc, mode = 'full' } = {}) {
  const normalized = normalizeTabs([tab])[0]
  const links = normalized?.links || []
  if (!links.length) return ''
  return links.map((link) => buildSingleHotspotDeviceCardHtml(link, { resolveSrc, mode })).join('')
}

function buildHotspotDeviceCarouselHtml(deviceCardHtml = '') {
  if (!deviceCardHtml) return ''
  return (
    '<div class="hotspot-device-carousel">'
    + '<button type="button" class="hotspot-device-carousel__nav hotspot-device-carousel__nav--prev" aria-label="上一个" disabled>&lsaquo;</button>'
    + `<div class="hotspot-linked-devices-preview">${deviceCardHtml}</div>`
    + '<button type="button" class="hotspot-device-carousel__nav hotspot-device-carousel__nav--next" aria-label="下一个">&rsaquo;</button>'
    + '</div>'
  )
}

const HOTSPOT_DEVICE_CAROUSEL_SCROLL_EPS = 2

function getHotspotDeviceCarouselStep(track) {
  const first = track?.querySelector?.('.hotspot-device-card, .rich-body-product-card-wrap')
  if (!first) return 210
  const gap = parseFloat(window.getComputedStyle(track).gap) || 10
  return first.offsetWidth + gap
}

export function updateHotspotDeviceCarousel(carousel) {
  const track = carousel?.querySelector?.('.hotspot-linked-devices-preview')
  const prev = carousel?.querySelector?.('.hotspot-device-carousel__nav--prev')
  const next = carousel?.querySelector?.('.hotspot-device-carousel__nav--next')
  if (!track || !prev || !next) return

  const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth)
  const canScroll = maxScroll > HOTSPOT_DEVICE_CAROUSEL_SCROLL_EPS
  carousel.classList.toggle('is-scrollable', canScroll)

  if (!canScroll) {
    prev.disabled = true
    next.disabled = true
    track.scrollLeft = 0
    return
  }

  prev.disabled = track.scrollLeft <= HOTSPOT_DEVICE_CAROUSEL_SCROLL_EPS
  next.disabled = track.scrollLeft >= maxScroll - HOTSPOT_DEVICE_CAROUSEL_SCROLL_EPS
}

export function scrollHotspotDeviceCarousel(carousel, direction = 0) {
  const track = carousel?.querySelector?.('.hotspot-linked-devices-preview')
  if (!track || !direction) return
  const step = getHotspotDeviceCarouselStep(track)
  track.scrollBy({ left: direction * step, behavior: 'smooth' })
}

export function initHotspotDeviceCarousels(root) {
  if (typeof document === 'undefined') return
  const scope = root?.querySelectorAll ? root : document
  const carousels = scope === document
    ? document.querySelectorAll('.hotspot-device-carousel')
    : scope.querySelectorAll('.hotspot-device-carousel')

  carousels.forEach((carousel) => {
    const track = carousel.querySelector('.hotspot-linked-devices-preview')
    const prev = carousel.querySelector('.hotspot-device-carousel__nav--prev')
    const next = carousel.querySelector('.hotspot-device-carousel__nav--next')
    if (!track || !prev || !next) return

    if (carousel.dataset.carouselBound !== '1') {
      carousel.dataset.carouselBound = '1'
      prev.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        scrollHotspotDeviceCarousel(carousel, -1)
      })
      next.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        scrollHotspotDeviceCarousel(carousel, 1)
      })
      track.addEventListener('scroll', () => updateHotspotDeviceCarousel(carousel), { passive: true })
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => updateHotspotDeviceCarousel(carousel))
        observer.observe(track)
      }
    }

    updateHotspotDeviceCarousel(carousel)
  })
}

function wrapHotspotDevicePreviewInCarousel(preview, doc) {
  if (!preview || preview.closest('.hotspot-device-carousel')) return
  const carousel = doc.createElement('div')
  carousel.className = 'hotspot-device-carousel'
  carousel.innerHTML =
    '<button type="button" class="hotspot-device-carousel__nav hotspot-device-carousel__nav--prev" aria-label="上一个" disabled>&lsaquo;</button>'
    + '<button type="button" class="hotspot-device-carousel__nav hotspot-device-carousel__nav--next" aria-label="下一个">&rsaquo;</button>'
  preview.before(carousel)
  carousel.insertBefore(preview, carousel.querySelector('.hotspot-device-carousel__nav--next'))
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function panelContentToHtml(content) {
  const value = String(content || '')
  if (!value) return ''
  if (/<[a-z][\s\S]*>/i.test(value)) {
    return ensureTableTabsDisplayHtml(upgradeLegacyTablesInHtml(value))
  }
  return value
    .split(/\n/)
    .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('')
}

function buildHotspotImageSwitcherHtml(imageCount, activeIndex = 0) {
  const count = Math.max(0, Number(imageCount) || 0)
  if (count <= 1) return ''
  const idx = Math.min(Math.max(0, Number(activeIndex) || 0), count - 1)
  const prevDisabled = idx <= 0 ? ' disabled' : ''
  const nextDisabled = idx >= count - 1 ? ' disabled' : ''
  let dots = ''
  for (let i = 0; i < count; i += 1) {
    const active = i === idx ? ' is-active' : ''
    dots += `<button type="button" class="hotspot-img-dot${active}" data-image-index="${i}" aria-label="图片${i + 1}"></button>`
  }
  return (
    '<div class="hotspot-img-switcher">'
    + `<button type="button" class="hotspot-img-nav hotspot-img-nav--prev" data-nav="prev" aria-label="上一张"${prevDisabled}>&lsaquo;</button>`
    + `<div class="hotspot-img-dots">${dots}</div>`
    + `<button type="button" class="hotspot-img-nav hotspot-img-nav--next" data-nav="next" aria-label="下一张"${nextDisabled}>&rsaquo;</button>`
    + '</div>'
  )
}

function updateHotspotImageSwitcherState(root, imageIndex) {
  const switcher = root?.querySelector?.('.hotspot-img-switcher')
  if (!switcher) return
  const count = root.querySelectorAll('.hotspot-img-slide').length
    || root.querySelectorAll('.hotspot-media-viewport > img.hotspot-img').length
  if (count <= 1) return
  const idx = Math.min(Math.max(0, Math.floor(Number(imageIndex) || 0)), count - 1)
  const prev = switcher.querySelector('.hotspot-img-nav--prev')
  const next = switcher.querySelector('.hotspot-img-nav--next')
  if (prev) prev.disabled = idx <= 0
  if (next) next.disabled = idx >= count - 1
  switcher.querySelectorAll('.hotspot-img-dot').forEach((el) => {
    const i = Number(el.getAttribute('data-image-index') || 0)
    el.classList.toggle('is-active', i === idx)
  })
}

function readCurrentHotspotImageIndex(root) {
  const fromAttr = Number(root?.getAttribute?.('data-active-image'))
  if (Number.isFinite(fromAttr) && fromAttr >= 0) return Math.floor(fromAttr)
  const activeDot = root?.querySelector?.('.hotspot-img-dot.is-active')
  if (activeDot) {
    const fromDot = Number(activeDot.getAttribute('data-image-index'))
    if (Number.isFinite(fromDot) && fromDot >= 0) return Math.floor(fromDot)
  }
  const activeSlide = root?.querySelector?.('.hotspot-img-slide.is-active')
  if (activeSlide) {
    const fromSlide = Number(activeSlide.getAttribute('data-image-index'))
    if (Number.isFinite(fromSlide) && fromSlide >= 0) return Math.floor(fromSlide)
  }
  return 0
}

function handleHotspotImageNavClick(root, delta) {
  if (!root || !delta) return
  const slides = root.querySelectorAll('.hotspot-img-slide')
  const count = slides.length || root.querySelectorAll('.hotspot-media-viewport > img.hotspot-img').length
  if (count <= 1) return
  const current = readCurrentHotspotImageIndex(root)
  const nextIdx = Math.min(Math.max(0, current + delta), count - 1)
  if (nextIdx === current) return
  const markers = Array.from(root.querySelectorAll('.hotspot-marker'))
  const currentSpot = markers.findIndex((el) => el.classList.contains('is-active'))
  const currentOnImage = currentSpot >= 0
    && readSpotImageIndex(root, currentSpot, markers) === nextIdx
  if (currentOnImage) {
    switchHotspotImage(root, nextIdx, { activeSpotIndex: currentSpot })
    return
  }
  const firstOnImage = markers.findIndex(
    (_el, i) => readSpotImageIndex(root, i, markers) === nextIdx,
  )
  if (firstOnImage >= 0) {
    activateHotspotTab(root, firstOnImage)
  } else {
    switchHotspotImage(root, nextIdx)
    activateHotspotDefault(root)
  }
}

function hotspotPanelHintText(index) {
  return formatViewingHotspot(index)
}

function buildHotspotPanelHintHtml(index, spotCount) {
  if (!spotCount) return ''
  return `<div class="hotspot-panel-hint">${escapeHtml(hotspotPanelHintText(index))}</div>`
}

/** 将 hotspotTabs 节点 attrs 转为门户展示 HTML（完整设备卡片） */
export function buildHotspotTabsHtml(attrs = {}, { resolveSrc, deviceCardMode = 'full', anchorIds } = {}) {
  const config = buildHotspotConfig(attrs)
  const resolve = typeof resolveSrc === 'function' ? resolveSrc : (src) => src || ''
  const images = (config.images?.length ? config.images : (config.src ? [config.src] : []))
    .map((item) => resolve(item))
    .filter(Boolean)
  const dataConfig = escapeHtml(JSON.stringify(config))
  const initialIndex = resolveHotspotInitialIndex(config)
  const initialImageIndex = initialIndex >= 0
    ? (Number(config.spots[initialIndex]?.imageIndex) || 0)
    : 0

  let mediaHtml = '<div class="hotspot-img-slides">'
  images.forEach((url, i) => {
    const active = i === initialImageIndex ? ' is-active' : ''
    mediaHtml += `<img src="${escapeHtml(url)}" class="hotspot-img hotspot-img-slide${active}" data-image-index="${i}" alt="">`
  })
  mediaHtml += '</div>'

  mediaHtml += config.spots
    .map((spot, index) => {
      const active = index === initialIndex ? ' is-active' : ''
      const imgIdx = Number(spot.imageIndex) || 0
      const hidden = imgIdx !== initialImageIndex ? 'display:none;' : ''
      return `<span class="hotspot-marker${active}" data-x="${spot.x}%" data-y="${spot.y}%" data-image-index="${imgIdx}" style="position:absolute;left:${spot.x}%;top:${spot.y}%;transform:translate(-50%,-50%);${hidden}">${index + 1}</span>`
    })
    .join('')

  mediaHtml = `<div class="hotspot-media-body">` +
    `<div class="hotspot-media-viewport">${mediaHtml}</div>`
  if (images.length > 1) {
    mediaHtml += buildHotspotImageSwitcherHtml(images.length, initialImageIndex)
  }
  mediaHtml += '</div>'
  mediaHtml += buildHotspotCertificationsHtml(config.certifications, {
    resolveSrc: resolve,
    align: config.certAlign,
  })

  const defaultActive = initialIndex < 0
  const defaultBody = panelContentToHtml(config.defaultContent)
  const spotCount = config.spots.length
  const defaultDeviceCardHtml = buildHotspotDeviceCardHtml(
    { links: config.defaultLinks },
    { resolveSrc: resolve, mode: deviceCardMode },
  )
  const defaultDevicesHtml = defaultDeviceCardHtml
    ? buildHotspotDeviceCarouselHtml(defaultDeviceCardHtml)
    : ''
  const defaultPanelHtml = (
    `<div class="hotspot-panel hotspot-panel--default${defaultActive ? ' is-active' : ''}" data-hotspot-default="1" style="${defaultActive ? '' : 'display:none;'}">` +
    buildHotspotPanelHintHtml(-1, spotCount) +
    `<div class="hotspot-panel-body">${defaultBody}</div>` +
    defaultDevicesHtml +
    `</div>`
  )

  const panelsHtml = defaultPanelHtml + config.tabs
    .map((tab, index) => {
      const active = index === initialIndex
      const title = escapeHtml(tab.title || `条目${index + 1}`)
      const body = panelContentToHtml(tab.content)
      const deviceCardHtml = buildHotspotDeviceCardHtml(tab, {
        resolveSrc: resolve,
        mode: deviceCardMode,
      })
      const devicesHtml = deviceCardHtml
        ? buildHotspotDeviceCarouselHtml(deviceCardHtml)
        : ''
      return `<div class="hotspot-panel${active ? ' is-active' : ''}" style="${active ? '' : 'display:none;'}">` +
        buildHotspotPanelHintHtml(index, spotCount) +
        `<div class="hotspot-panel-title">${title}</div>` +
        `<div class="hotspot-panel-body">${body}</div>` +
        devicesHtml +
        `</div>`
    })
    .join('')

  const pos = config.mediaPosition || 'left'
  const certAlign = normalizeCertAlign(config.certAlign)
  const headingHtml = buildHotspotHeadingHtml(config, { anchorIds })
  return (
    `<div data-type="${HOTSPOT_TABS_TYPE}" data-config="${dataConfig}" data-media-position="${pos}" data-cert-align="${certAlign}" class="hotspot-tabs is-media-${pos}">` +
    headingHtml +
    `<div class="hotspot-media" style="position:relative;">${mediaHtml}</div>` +
    `<div class="hotspot-side">` +
    `<div class="hotspot-detail">${panelsHtml}</div>` +
    `</div></div>`
  )
}

export function repairHotspotHtml(html) {
  if (!html || !html.includes('hotspot-tabs')) return html
  try {
    const parsed = parseHtmlFragment(html, '__hotspot_root')
    if (!parsed?.root) return html
    const { doc, root: fragment } = parsed
    const anchorIds = new Set()
    fragment.querySelectorAll(`div[data-type="${HOTSPOT_TABS_TYPE}"]`).forEach((root) => {
      let spots = []
      let tabs = []
      let defaultContent = ''
      let headingConfig = null
      const raw = root.getAttribute('data-config')
      if (raw) {
        try {
          const config = JSON.parse(raw)
          headingConfig = buildHotspotConfig(config)
          spots = Array.isArray(headingConfig?.spots) ? headingConfig.spots : []
          tabs = Array.isArray(headingConfig?.tabs) ? headingConfig.tabs : []
          defaultContent = typeof headingConfig?.defaultContent === 'string' ? headingConfig.defaultContent : ''
        } catch {
          spots = []
          tabs = []
          defaultContent = ''
          headingConfig = null
        }
      }
      if (headingConfig) syncHotspotHeadingElement(root, headingConfig, doc, anchorIds)
      root.querySelectorAll('.hotspot-marker').forEach((el, index) => {
        const spot = spots[index]
        const x = spot?.x ?? parseFloat(String(el.getAttribute('data-x') || '').replace('%', ''))
        const y = spot?.y ?? parseFloat(String(el.getAttribute('data-y') || '').replace('%', ''))
        if (!Number.isFinite(x) || !Number.isFinite(y)) return
        const imgIdx = Number(spot?.imageIndex ?? el.getAttribute('data-image-index') ?? 0) || 0
        el.setAttribute('data-image-index', String(imgIdx))
        el.style.setProperty('position', 'absolute')
        el.style.setProperty('left', `${x}%`)
        el.style.setProperty('top', `${y}%`)
        el.style.setProperty('transform', 'translate(-50%, -50%)')
      })
      const defaultBody = root.querySelector('.hotspot-panel[data-hotspot-default] .hotspot-panel-body')
      if (defaultBody && defaultContent) {
        defaultBody.innerHTML = relocateInlineStylesInHtml(panelContentToHtml(defaultContent))
      }
      Array.from(root.querySelectorAll('.hotspot-panel:not([data-hotspot-default])')).forEach((panel, index) => {
        const el = panel.querySelector('.hotspot-panel-body')
        const content = tabs[index]?.content
        if (!el || content == null || content === '') return
        el.innerHTML = relocateInlineStylesInHtml(panelContentToHtml(content))
      })
      root.querySelectorAll('.hotspot-panel').forEach((panel) => {
        if (panel.querySelector(':scope > .hotspot-linked-devices-preview')) return
        const cards = Array.from(panel.children).filter(
          (el) => el.classList?.contains('hotspot-device-card'),
        )
        if (!cards.length) return
        const wrap = doc.createElement('div')
        wrap.className = 'hotspot-linked-devices-preview'
        cards[0].before(wrap)
        cards.forEach((card) => wrap.appendChild(card))
      })
      root.querySelectorAll('.hotspot-panel > .hotspot-linked-devices-preview').forEach((preview) => {
        wrapHotspotDevicePreviewInCarousel(preview, doc)
      })
    })
    return fragment.innerHTML
  } catch {
    return html
  }
}

function readSpotImageIndex(root, index, markers = []) {
  try {
    const config = JSON.parse(root?.getAttribute?.('data-config') || '{}')
    const fromConfig = Number(config?.spots?.[index]?.imageIndex)
    if (Number.isFinite(fromConfig) && fromConfig >= 0) return Math.floor(fromConfig)
  } catch {
    // ignore
  }
  const fromMarker = Number(markers[index]?.getAttribute?.('data-image-index'))
  if (Number.isFinite(fromMarker) && fromMarker >= 0) return Math.floor(fromMarker)
  const fromItem = Number(
    root?.querySelectorAll?.('.hotspot-list-item')?.[index]?.getAttribute?.('data-image-index'),
  )
  if (Number.isFinite(fromItem) && fromItem >= 0) return Math.floor(fromItem)
  return 0
}

export function switchHotspotImage(root, imageIndex, { activeSpotIndex } = {}) {
  if (!root) return
  const idx = Math.max(0, Math.floor(Number(imageIndex) || 0))
  root.setAttribute('data-active-image', String(idx))
  const slides = root.querySelectorAll('.hotspot-img-slide')
  if (slides.length) {
    slides.forEach((el) => {
      const i = Number(el.getAttribute('data-image-index') || 0)
      const on = i === idx
      el.classList.toggle('is-active', on)
      el.style.opacity = on ? '1' : '0'
      el.style.visibility = on ? 'visible' : 'hidden'
      el.style.zIndex = on ? '1' : '0'
      el.style.pointerEvents = on ? 'auto' : 'none'
    })
  } else {
    root.querySelectorAll('.hotspot-media > img.hotspot-img').forEach((el, i) => {
      const on = i === idx
      el.classList.toggle('is-active', on)
      el.style.display = on ? '' : 'none'
    })
  }
  root.querySelectorAll('.hotspot-img-dot').forEach((el) => {
    const i = Number(el.getAttribute('data-image-index') || 0)
    el.classList.toggle('is-active', i === idx)
  })
  updateHotspotImageSwitcherState(root, idx)
  root.querySelectorAll('.hotspot-marker').forEach((el, i) => {
    const imgIdx = Number(el.getAttribute('data-image-index') || 0)
    el.style.display = imgIdx === idx ? '' : 'none'
    if (typeof activeSpotIndex === 'number') {
      el.classList.toggle('is-active', i === activeSpotIndex)
    }
  })
}

/** 展示态：回到默认内容（取消标点选中） */
export function activateHotspotDefault(root) {
  if (!root) return
  root.querySelectorAll('.hotspot-marker').forEach((el) => {
    el.classList.remove('is-active')
  })
  root.querySelectorAll('.hotspot-list-item').forEach((el) => {
    el.classList.remove('is-active')
  })
  const defaultPanel = root.querySelector('.hotspot-panel[data-hotspot-default]')
  const tabPanels = Array.from(root.querySelectorAll('.hotspot-panel:not([data-hotspot-default])'))
  if (defaultPanel) {
    defaultPanel.classList.add('is-active')
    defaultPanel.style.display = ''
  }
  tabPanels.forEach((el) => {
    el.classList.remove('is-active')
    el.style.display = 'none'
  })
}

export function activateHotspotTab(root, index) {
  if (!root) return
  const markers = Array.from(root.querySelectorAll('.hotspot-marker'))
  const tabPanels = Array.from(root.querySelectorAll('.hotspot-panel:not([data-hotspot-default])'))
  const defaultPanel = root.querySelector('.hotspot-panel[data-hotspot-default]')
  const imageIndex = readSpotImageIndex(root, index, markers)
  switchHotspotImage(root, imageIndex, { activeSpotIndex: index })
  root.querySelectorAll('.hotspot-list-item').forEach((el, i) => {
    el.classList.toggle('is-active', i === index)
  })
  if (defaultPanel) {
    defaultPanel.classList.remove('is-active')
    defaultPanel.style.display = 'none'
  }
  tabPanels.forEach((el, i) => {
    el.classList.toggle('is-active', i === index)
    el.style.display = i === index ? '' : 'none'
    if (i === index) initHotspotDeviceCarousels(el)
  })
}

let staticHotspotBound = false

export function bindStaticHotspotTabs() {
  if (staticHotspotBound || typeof document === 'undefined') return
  staticHotspotBound = true
  document.addEventListener('click', (event) => {
    if (event.target?.closest?.('.hotspot-tabs-node')) return

    const dot = event.target?.closest?.('.hotspot-img-dot')
    if (dot) {
      const root = dot.closest('.hotspot-tabs')
      if (!root) return
      event.preventDefault()
      const imageIndex = Number(dot.getAttribute('data-image-index') || 0) || 0
      const markers = Array.from(root.querySelectorAll('.hotspot-marker'))
      const currentIndex = markers.findIndex((el) => el.classList.contains('is-active'))
      const currentOnImage = currentIndex >= 0
        && readSpotImageIndex(root, currentIndex, markers) === imageIndex
      if (currentOnImage) {
        switchHotspotImage(root, imageIndex, { activeSpotIndex: currentIndex })
        return
      }
      const firstOnImage = markers.findIndex(
        (_el, i) => readSpotImageIndex(root, i, markers) === imageIndex,
      )
      if (firstOnImage >= 0) {
        activateHotspotTab(root, firstOnImage)
      } else {
        switchHotspotImage(root, imageIndex)
        activateHotspotDefault(root)
      }
      return
    }

    const nav = event.target?.closest?.('.hotspot-img-nav')
    if (nav) {
      const root = nav.closest('.hotspot-tabs')
      if (!root || nav.disabled) return
      event.preventDefault()
      const delta = nav.classList.contains('hotspot-img-nav--prev') ? -1 : 1
      handleHotspotImageNavClick(root, delta)
      return
    }

    const marker = event.target?.closest?.('.hotspot-marker')
    if (marker) {
      const root = marker.closest('.hotspot-tabs')
      if (!root) return
      const markers = Array.from(root.querySelectorAll('.hotspot-marker'))
      const index = markers.indexOf(marker)
      if (index >= 0) activateHotspotTab(root, index)
      return
    }

    // 点击图片空白处：回到默认内容
    const img = event.target?.closest?.('.hotspot-img, .hotspot-media-viewport')
    if (!img) return
    const root = img.closest('.hotspot-tabs')
    if (!root || event.target?.closest?.('.hotspot-media-tools, .hotspot-img-switcher')) return
    activateHotspotDefault(root)
  })

  initHotspotDeviceCarousels(document)
}
